import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const ORGANISME_ORDER = ['SAR', 'AMAIR', 'ADAR'];

const GROUPEMENTS = {
  SAR: {
    nom: 'Syndicat Apicole de la Réunion',
    logo: '/logos/logo_sar.png',
    color: '#ffc34a',
    colorSoft: '#ffe1a3',
    heading: '#6e4e02',
    onPrimary: '#ffffff',
    documents: [
      { id: 'statuts', label: 'Statuts', href: '/documents/SAR_Statuts.pdf' },
      { id: 'ri', label: 'Règlement intérieur', href: '/documents/SAR_Reglement-interieur.pdf' },
    ],
  },
  AMAIR: {
    nom: "Association de la Maison de l'Apiculture de l'Île de la Réunion",
    logo: '/logos/logo_amair.png',
    color: '#70e65e',
    colorSoft: '#d3fccc',
    heading: '#386602',
    onPrimary: '#1a2e00',
    documents: [
      { id: 'statuts', label: 'Statuts', href: '/documents/AMAIR_Statuts.pdf' },
      { id: 'ri', label: 'Règlement intérieur', href: '/documents/AMAIR_Reglement-interieur.pdf' },
      {
        id: 'ri_miellerie',
        label: 'Règlement intérieur de la miellerie',
        href: '/documents/AMAIR_Miellerie_Reglement-interieur.pdf',
      },
    ],
  },
  ADAR: {
    nom: "Association pour le Développement de l'Apiculture à l'île de la Réunion",
    logo: '/logos/logo_adar.png',
    color: '#0B4395',
    colorSoft: '#E7EEF6',
    heading: '#0B4395',
    onPrimary: '#ffffff',
    documents: [
      { id: 'statuts', label: 'Statuts', href: '/documents/ADAR_Statuts.pdf' },
      { id: 'ri', label: 'Règlement intérieur', href: '/documents/ADAR_Reglement-interieur.pdf' },
    ],
  },
};

const SERVICE_LABELS = {
  miellerie: 'Miellerie',
  assurance_unaf: "Services de l'UNAF",
};

const ADHESION_STATUS = {
  en_cours: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
  paiement_demande: { label: 'Paiement demandé', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
  en_attente: { label: 'En attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
  expiree: { label: 'Expiré', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon },
};

const SERVICE_STATUS = {
  en_attente_paiement: { label: 'Paiement en attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  en_attente_caution: {
    label: 'Caution en attente',
    color: 'bg-red-100 text-red-800',
    icon: ExclamationTriangleIcon,
    hint: "Veuillez remettre votre chèque de caution de 300€ au responsable lors de votre première visite. À défaut, l'accès à la miellerie ne pourra pas vous être accordé.",
  },
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  en_attente_validation: { label: 'Validation en attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  expire: { label: 'Expiré', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon },
};

function StatusBadge({ status, kind = 'adhesion' }) {
  const [hintOpen, setHintOpen] = useState(false);
  const configMap = kind === 'service' ? SERVICE_STATUS : ADHESION_STATUS;
  const config = configMap[status] || {
    label: status,
    color: 'bg-slate-100 text-slate-800',
    icon: DocumentTextIcon,
  };
  const Icon = config.icon;
  const badgeClass = `inline-flex items-center pr-3.5 pl-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.color}`;

  if (!config.hint) {
    return (
      <span className={badgeClass}>
        <Icon className="w-3.5 h-3.5 mr-1" />
        {config.label}
      </span>
    );
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setHintOpen((open) => !open)}
        className={`${badgeClass} cursor-pointer hover:brightness-95`}
        aria-expanded={hintOpen}
      >
        <Icon className="w-3.5 h-3.5 mr-1" />
        {config.label}
      </button>
      {hintOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setHintOpen(false)} />
          <div
            role="tooltip"
            className="absolute z-20 right-0 top-full mt-1.5 w-64 rounded-lg bg-white px-3 py-2 text-xs leading-snug text-gray-700 shadow-lg border border-gray-200"
          >
            {config.hint}
          </div>
        </>
      )}
    </span>
  );
}

function canViewGroupementDocuments(adhesion) {
  return adhesion && ['actif', 'expiree'].includes(adhesion.status);
}

function isLegacyAdhesion(adhesion) {
  return adhesion?.status === 'expiree' && adhesion.annee < 2026;
}

function DocumentsModal({ open, onClose, sections }) {
  if (!open) return null;

  const hasDocs = sections.some((section) => section.documents.length > 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
          <div className="bg-honey-50 px-6 py-4 border-b border-honey-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-honey-100 rounded-full flex items-center justify-center">
                <FolderIcon className="h-6 w-6 text-honey-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fermer"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
            {hasDocs ? (
              <div className="space-y-6">
                {sections
                  .filter((section) => section.documents.length > 0)
                  .map((section) => (
                    <div key={section.organisme}>
                      <div className="flex items-center gap-2 mb-3">
                        <img src={section.logo} alt="" className="h-8 w-auto object-contain" />
                        <h4 className="text-sm font-semibold text-gray-900">{section.organisme}</h4>
                      </div>
                      <ul className="space-y-2">
                        {section.documents.map((doc) => (
                          <li key={doc.id}>
                            <a
                              href={doc.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-800 hover:bg-honey-50 hover:border-honey-200 transition-colors"
                            >
                              <span className="flex items-center min-w-0">
                                <DocumentTextIcon className="h-4 w-4 text-honey-600 mr-2 flex-shrink-0" />
                                <span className="truncate">{doc.label}</span>
                              </span>
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 ml-3 flex-shrink-0" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Les documents des groupements seront disponibles une fois votre adhésion active.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MesAdhesions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [adhesions, setAdhesions] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redirectMessage, setRedirectMessage] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [serviceSettings, setServiceSettings] = useState({ miellerieActif: true, unafActif: true });
  const [unafContext, setUnafContext] = useState(null);
  const [nextUnafExportDate, setNextUnafExportDate] = useState(null);
  const [documentsModal, setDocumentsModal] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (location.state?.message) {
      setRedirectMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
    fetchPageData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, location.state]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const api = process.env.REACT_APP_API_URL;

      const [adhesionsResponse, servicesResponse, settingsResponse, unafResponse] = await Promise.all([
        axios.get(`${api}/adhesions/my-adhesions`, { headers }),
        axios.get(`${api}/services/my-services`, { headers }).catch((err) => {
          console.error('Erreur lors du chargement des services:', err);
          return { data: [] };
        }),
        axios.get(`${api}/service-settings`).catch((err) => {
          console.error('Erreur lors du chargement des paramètres de services:', err);
          return { data: { miellerieActif: true, unafActif: true } };
        }),
        axios.get(`${api}/services/can-subscribe/assurance-unaf`, { headers }).catch((err) => {
          console.error('Erreur lors du chargement du contexte UNAF:', err);
          return { data: null };
        }),
      ]);

      const adhesionsList = Array.isArray(adhesionsResponse.data)
        ? adhesionsResponse.data
        : adhesionsResponse.data.adhesions || [];
      const servicesList = servicesResponse.data || [];

      setAdhesions(adhesionsList);
      setServices(servicesList);
      setServiceSettings(settingsResponse.data || { miellerieActif: true, unafActif: true });
      setUnafContext(unafResponse.data);

      const years = getAvailableYears(adhesionsList, servicesList);
      const currentYear = new Date().getFullYear();
      setYearFilter((prev) => {
        if (years.includes(prev)) return prev;
        if (years.includes(currentYear)) return currentYear;
        return years[0] ?? currentYear;
      });

      if (unafResponse.data) {
        const exportYear =
          unafResponse.data.anneeCampagne ||
          unafResponse.data.openCampaignYears?.[0] ||
          currentYear;
        fetchNextUnafExportDate(exportYear);
      }
    } catch (err) {
      setError('Erreur lors du chargement des adhésions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextUnafExportDate = async (annee) => {
    try {
      const year = annee || new Date().getFullYear();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/next-unaf-export?annee=${year}`);
      setNextUnafExportDate(response.data.nextExportDateFormatted);
    } catch (err) {
      console.error("Erreur lors du chargement de la prochaine date d'export UNAF:", err);
    }
  };

  const visibleAdhesions = adhesions.filter((a) => a.status !== 'abandonnee');
  const visibleServices = services.filter((s) => s.status !== 'abandonnee');
  const availableYears = getAvailableYears(visibleAdhesions, visibleServices);

  const getAdhesionFor = (organisme, annee) =>
    visibleAdhesions.find((a) => a.organisme === organisme && a.annee === annee);

  const getServicesFor = (organisme, annee) =>
    visibleServices.filter((s) => s.organisme === organisme && s.annee === annee);

  const canSubscribeMiellerie = (annee) => {
    if (annee !== new Date().getFullYear()) return false;
    if (!serviceSettings.miellerieActif) return false;
    const adhesion = getAdhesionFor('AMAIR', annee);
    if (!adhesion || adhesion.status !== 'actif') return false;
    return !getServicesFor('AMAIR', annee).some((s) => s.typeService === 'miellerie');
  };

  const canSubscribeUnaf = (annee) => {
    if (!serviceSettings.unafActif) return false;
    return Boolean((unafContext?.openCampaigns || []).find((c) => c.annee === annee && c.canSubscribe));
  };

  const groupementCards = ORGANISME_ORDER.map((organisme) => {
    const adhesion = getAdhesionFor(organisme, yearFilter);
    const groupServices = getServicesFor(organisme, yearFilter);
    const subscribeMiellerie = organisme === 'AMAIR' && canSubscribeMiellerie(yearFilter);
    const subscribeUnaf = organisme === 'SAR' && canSubscribeUnaf(yearFilter);

    if (!adhesion && groupServices.length === 0 && !subscribeMiellerie && !subscribeUnaf) {
      return null;
    }

    return {
      organisme,
      meta: GROUPEMENTS[organisme],
      adhesion,
      services: groupServices,
      subscribeMiellerie,
      subscribeUnaf,
      documents: canViewGroupementDocuments(adhesion) ? GROUPEMENTS[organisme].documents : [],
    };
  }).filter(Boolean);

  const documentSections = (organismeFilter) =>
    groupementCards
      .filter((card) => !organismeFilter || card.organisme === organismeFilter)
      .map((card) => ({
        organisme: card.organisme,
        logo: card.meta.logo,
        documents: card.documents,
      }));

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <Link to="/dashboard" className="hover:text-honey-600">
            Espace personnel
          </Link>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="font-medium text-gray-900">Mes adhésions</span>
        </div>
      </div>

      <div className="px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mes adhésions</h1>
            <p className="mt-2 text-sm text-gray-600">
              Vos groupements, services associés et documents.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setDocumentsModal('all')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FolderIcon className="-ml-1 mr-2 h-5 w-5 text-honey-600" />
              Documents
            </button>
            <Link
              to="/adhesion"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouvelle adhésion
            </Link>
          </div>
        </div>
      </div>

      <div className="px-8 pb-8">
        {redirectMessage && (
          <div className="mb-6 rounded-lg bg-honey-50 border border-honey-200 p-4">
            <p className="text-sm text-honey-800">{redirectMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {availableYears.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {availableYears.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => setYearFilter(year)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  yearFilter === year
                    ? 'bg-honey-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        )}

        {visibleAdhesions.length === 0 && visibleServices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-16 text-center">
            <DocumentTextIcon className="mx-auto h-16 w-16 text-honey-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Aucune adhésion</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              Vous n&apos;avez pas encore d&apos;adhésion en cours. Commencez par adhérer à un groupement pour accéder à ses services et documents.
            </p>
            <div className="mt-8">
              <Link
                to="/adhesion"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600 shadow-sm"
              >
                Créer ma première adhésion
              </Link>
            </div>
          </div>
        ) : groupementCards.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-12 text-center">
            <h3 className="text-lg font-medium text-gray-900">Aucune adhésion pour {yearFilter}</h3>
            <p className="mt-2 text-sm text-gray-500">
              Changez d&apos;année ou créez une nouvelle adhésion.
            </p>
            <Link
              to="/adhesion"
              className="inline-flex mt-6 items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
            >
              Nouvelle adhésion
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {groupementCards.map((card) => (
              <GroupementCard
                key={card.organisme}
                card={card}
                year={yearFilter}
                nextUnafExportDate={nextUnafExportDate}
                onOpenDocuments={() => setDocumentsModal(card.organisme)}
              />
            ))}
            <Link
              to="/adhesion"
              className="min-h-[16rem] rounded-xl border-2 border-dashed border-gray-300 bg-white/60 hover:border-honey-400 hover:bg-honey-50/40 transition-colors flex flex-col items-center justify-center px-6 py-8 text-center"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-400 mb-3">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <span className="text-base font-semibold text-gray-700">Nouvelle adhésion</span>
              <span className="mt-1 text-sm text-gray-500">Adhérer à un autre groupement</span>
            </Link>
          </div>
        )}
      </div>

      <DocumentsModal
        open={Boolean(documentsModal)}
        onClose={() => setDocumentsModal(null)}
        sections={documentSections(documentsModal === 'all' ? null : documentsModal)}
      />
    </div>
  );
}

function getAvailableYears(adhesionsList, servicesList) {
  return [...new Set([
    ...adhesionsList.filter((a) => a.status !== 'abandonnee').map((a) => a.annee),
    ...servicesList.filter((s) => s.status !== 'abandonnee').map((s) => s.annee),
  ])].sort((a, b) => b - a);
}

function GroupementCard({ card, year, nextUnafExportDate, onOpenDocuments }) {
  const { organisme, meta, adhesion, services, subscribeMiellerie, subscribeUnaf, documents } = card;
  const showServices = services.length > 0 || subscribeMiellerie || subscribeUnaf;
  const outlineBtn = {
    color: meta.heading,
    borderColor: meta.color,
    backgroundColor: '#ffffff',
  };
  const solidBtn = {
    backgroundColor: meta.color,
    color: meta.onPrimary,
  };

  return (
    <div
      className="bg-white rounded-xl shadow-sm flex flex-col border overflow-visible"
      style={{ borderColor: "#d1d5db" }}
    >
      <div
        className="px-6 py-4 border-b rounded-t-xl"
        style={{ backgroundColor: meta.colorSoft, borderBottomColor: meta.color }}
      >
        <div className="flex items-center gap-6 min-w-0">
          <img src={meta.logo} alt="" className="h-16 w-auto object-contain flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-xl font-bold">{organisme}</h2>
            <p className="text-xs leading-snug">{meta.nom}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5 flex-1">
        <section className="flex gap-2 items-center justify-between border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm tracking-wide font-semibold text-slate-800">Adhésion {year}</h3>
          </div>
          {adhesion ? (
            <div className="flex items-center justify-between gap-2">
              <StatusBadge status={adhesion.status} />
              {!isLegacyAdhesion(adhesion) && (
                <Link
                  to={`/adhesion/${adhesion._id}`}
                  className="block w-full text-center px-3 pt-1.5 pb-1 text-sm font-medium border rounded-md hover:opacity-90 transition-opacity"
                  style={outlineBtn}
                >
                  Détails
                </Link>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Pas d&apos;adhésion pour cette année.</p>
          )}
        </section>

        {showServices && (
          <section className="border-b border-gray-200 pb-4">
            <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Services associés</p>
            <div className="space-y-3">
              {services.map((service) => (
                <div key={service._id}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {SERVICE_LABELS[service.typeService] || service.nom || service.typeService}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge status={service.status} kind="service" />
                      <Link
                        to={`/service/${service._id}`}
                        className="block w-full text-center px-3 py-1.5 text-sm font-medium border rounded-md hover:opacity-90 transition-opacity"
                        style={outlineBtn}
                      >
                        Détails
                      </Link>
                    </div>
                  </div>
                </div>
              ))}

              {subscribeMiellerie && (
                <div className="rounded-lg border border-dashed p-3 flex items-center justify-between gap-2" style={{ borderColor: meta.color }}>
                  <h3 className="text-sm font-semibold tracking-wide text-slate-800">Miellerie</h3>
                  <Link
                    to="/service/miellerie"
                    className="block text-center px-7 py-1.5 text-sm font-medium rounded-md hover:opacity-90 transition-opacity"
                    style={solidBtn}
                  >
                    Souscrire
                  </Link>
                </div>
              )}

              {subscribeUnaf && (
                <div className="rounded-lg border border-dashed p-3" style={{ borderColor: meta.color }}>
                  <h3 className="text-sm font-semibold tracking-wide text-slate-800">Services de l&apos;UNAF</h3>
                  <p className="text-xs text-gray-500 mt-1">Assurance, revue, éco-contribution…</p>
                  {nextUnafExportDate && (
                    <p className="text-xs text-blue-700 mt-1 flex items-start">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0" />
                      Activation le {nextUnafExportDate}
                    </p>
                  )}
                  <Link
                    to={`/service/assurance-unaf?annee=${year}`}
                    className="mt-3 block w-full text-center px-3 py-1.5 text-sm font-medium rounded-md hover:opacity-90 transition-opacity"
                    style={solidBtn}
                  >
                    Souscrire
                  </Link>
                </div>
              )}
            </div>
          </section>
        )}

        <section>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">Documents</p>
          {documents.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {documents.slice(0, 2).map((doc) => (
                <a
                  key={doc.id}
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border hover:opacity-90 transition-opacity"
                  style={{ color: meta.heading, backgroundColor: meta.colorSoft, borderColor: meta.color }}
                >
                  {doc.label}
                </a>
              ))}
              <button
                type="button"
                onClick={onOpenDocuments}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
              >
                Tous
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Disponibles une fois l&apos;adhésion active.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
